import React, { useId, useState } from 'react';
import { ChevronUp, Moon, Sun } from 'lucide-react';

const MobileUserMenu = ({
  user,
  onLogout,
  onThemeChange,
  onNavigate,
  canAccessSettings,
  canAccessRequests,
  pendingCount,
  currentTheme,
  accentColor
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();
  const contentTabIndex = isExpanded ? 0 : -1;

  const displayName = user?.full_name || user?.name || 'Usuário';
  const displayEmail = user?.corporate_email || user?.email || 'Conta corporativa';
  const avatarText = (user?.full_name?.charAt(0) || user?.name?.charAt(0) || 'U').toUpperCase();

  return (
    <section className="rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)]">
      <button
        type="button"
        onClick={() => setIsExpanded((value) => !value)}
        className={`flex min-h-[48px] w-full items-center justify-between gap-3 rounded-3xl px-4 py-3 text-left transition-all duration-200 hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] ${
          isExpanded ? 'bg-[color:var(--surface-muted)]' : ''
        }`}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        aria-label="Abrir menu do usuário"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-[color:var(--avatar-text)]"
            style={{ background: accentColor || '#0f172a' }}
          >
            {avatarText}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-[color:var(--text-primary)]">{displayName}</span>
          </span>
        </span>
        <ChevronUp
          className={`h-4 w-4 shrink-0 text-[color:var(--text-muted)] transition-transform duration-200 ${
            isExpanded ? 'rotate-180 text-[color:var(--text-primary)]' : ''
          }`}
        />
      </button>

      <div
        id={contentId}
        aria-hidden={!isExpanded}
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">
            <div className="pb-3">
              <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">{displayName}</p>
              <p className="truncate text-xs text-[color:var(--text-muted)]">{displayEmail}</p>
            </div>

            <div className="h-px bg-[color:var(--surface-muted)]" />

            <div className="space-y-2 py-3">
              <button
                type="button"
                onClick={() => onNavigate?.('/perfil')}
                tabIndex={contentTabIndex}
                className="w-full rounded-2xl px-3 py-3 text-left text-sm text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
              >
                Meu perfil
              </button>
              {canAccessSettings && (
                <button
                  type="button"
                  onClick={() => onNavigate?.('/configuracoes')}
                  tabIndex={contentTabIndex}
                  className="w-full rounded-2xl px-3 py-3 text-left text-sm text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
                >
                  Configurações
                </button>
              )}
              {canAccessRequests && (
                <button
                  type="button"
                  onClick={() => onNavigate?.('/gestao-solicitacoes')}
                  tabIndex={contentTabIndex}
                  className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left text-sm text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
                >
                  Gestão de solicitações
                  {pendingCount > 0 && (
                    <span className="ml-auto rounded-full bg-red-500/85 px-2 py-0.5 text-[10px] font-semibold text-white">
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                  )}
                </button>
              )}
            </div>

            <div className="h-px bg-[color:var(--surface-muted)]" />

            <div className="py-3">
              <p className="px-3 text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">Tema</p>
              <div className="mt-2 flex rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-1">
                <button
                  type="button"
                  onClick={() => onThemeChange?.('light')}
                  tabIndex={contentTabIndex}
                  className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition ${
                    currentTheme === 'light'
                      ? 'text-white shadow-sm'
                      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                  }`}
                  style={currentTheme === 'light' ? { background: accentColor } : undefined}
                >
                  <Sun className="mr-1 inline h-3.5 w-3.5" />
                  Claro
                </button>
                <button
                  type="button"
                  onClick={() => onThemeChange?.('dark')}
                  tabIndex={contentTabIndex}
                  className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition ${
                    currentTheme === 'dark'
                      ? 'text-white shadow-sm'
                      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                  }`}
                  style={currentTheme === 'dark' ? { background: accentColor } : undefined}
                >
                  <Moon className="mr-1 inline h-3.5 w-3.5" />
                  Escuro
                </button>
              </div>
            </div>

            <div className="h-px bg-[color:var(--surface-muted)]" />

            <div className="pt-3">
              <button
                type="button"
                onClick={onLogout}
                tabIndex={contentTabIndex}
                className="w-full rounded-2xl px-3 py-3 text-left text-sm font-semibold text-[color:var(--status-bad)] transition hover:bg-rose-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileUserMenu;
